import React, { useState, useEffect } from "react";
import classes from "./CashReciept.module.css";
import api, { withCompanyId } from "../../../Components/API/Api";
import { useNavigate } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import Swal from "sweetalert2";
import crop from "../../../Assets/repoort.png";
import verified from "../../../Assets/verified.svg";

function CashReciept({ isOpen, onClose, onSuccess }) {
  const [rows, setRows] = useState([
    { accountId: "", accountDisplay: "", accountCode: "", amount: "", showDropdown: false, accountLocked: false, invoiceItemId: "", itemId: "" },
  ]);

  const [reference, setReference] = useState("");
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [transactionDate, setTransactionDate] = useState("");
  const [paymentSearch, setPaymentSearch] = useState("");
  const [showPaymentDropdown, setShowPaymentDropdown] = useState(false);
  const [debitSearch, setDebitSearch] = useState("");
  const [showDebitDropdown, setShowDebitDropdown] = useState(false);

  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectableAccounts, setSelectableAccounts] = useState([]);

  // Create rows from an invoice `raw` object. Returns an array of row objects.
  // Accounts are populated directly from the receivable_gl in the invoice item data.
  const createRowsFromInvoice = (raw) => {
    const items = Array.isArray(raw?.items) && raw.items.length > 0 ? raw.items : [];

    if (items.length > 0) {
      return items.map((it) => {
        // Use receivable_gl directly from invoice item (not from a pre-fetched accounts list)
        return {
          accountId: it.receivable_gl?.id ?? "",
          accountDisplay: it.receivable_gl?.gl_name ?? "",
          accountCode: it.receivable_gl?.gl_code ?? "",
          amount: String(it.balance ?? it.amount ?? ""),
          showDropdown: false,
          accountLocked: true,
          invoiceItemId: it.id ?? "",
          itemId: it.item_id ?? "",
        };
      });
    }

    // Fallback: if no items, try to get receivable_gl from first item if available
    const firstItem = raw?.items?.[0];
    if (firstItem?.receivable_gl) {
      return [
        {
          accountId: firstItem.receivable_gl.id ?? "",
          accountDisplay: firstItem.receivable_gl.gl_name ?? "",
          accountCode: firstItem.receivable_gl.gl_code ?? "",
          amount: String(raw?.balance ?? raw?.amount ?? ""),
          showDropdown: false,
          accountLocked: true,
          invoiceItemId: "",
          itemId: "",
        },
      ];
    }

    // Ultimate fallback: empty row (no items, so no receivable_gl available)
    return [
      {
        accountId: "",
        accountDisplay: "",
        accountCode: "",
        amount: String(raw?.balance ?? raw?.amount ?? ""),
        showDropdown: false,
        accountLocked: true,
        invoiceItemId: "",
        itemId: "",
      },
    ];
  };
  const [isSaving, setIsSaving] = useState(false);
  const [description, setDescription] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [debitAccount, setDebitAccount] = useState("");
  const [outstandingAmount, setOutstandingAmount] = useState("");
  const [selectedLoanAccount, setSelectedLoanAccount] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState("");

  const [totalAmount, setTotalAmount] = useState("");
  const [receiptTypes, setReceiptTypes] = useState([]);
  const [receiptTypesLoading, setReceiptTypesLoading] = useState(false);

  const [expenseOptions, setExpenseOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [receiptType, setReceiptType] = useState("");
  const [selectedCurrency, setSelectedCurrency] = useState("");
  const [selectedPaymentMode, setSelectedPaymentMode] = useState("");
  const [supportingDocument, setSupportingDocument] = useState(null);
  const [supportingError, setSupportingError] = useState("");

  // Initialize reference number from description, but let user override it
  useEffect(() => {
    setReferenceNumber((prev) => (prev ? prev : description));
  }, [description]);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setSupportingDocument(null);
      setSupportingError("");
      return;
    }
    if (file.type !== 'application/pdf') {
      setSupportingError('Only PDF files are allowed.');
      setSupportingDocument(null);
      return;
    }
    const max = 2 * 1024 * 1024; // 2MB
    if (file.size > max) {
      setSupportingError('File must be 2MB or smaller.');
      setSupportingDocument(null);
      return;
    }
    setSupportingError("");
    setSupportingDocument(file);
  };

  const handlePrinter = () => {
    const receiptData = {
      reference,
      transactionDate,
      description,
      referenceNumber,
      selectedCurrency,
      selectedPaymentMode,
      debitAccount,
      totalAmount,
      rows,
    };

    navigate("/preview", {
      state: { invoice: receiptData },
    });
  };

  useEffect(() => {
    const fetchPendingInvoices = async () => {
      if (!receiptType?.toLowerCase()?.includes("credit customer")) return;

      try {
        setCustomerLoading(true);
        const res = await api.get('/v1/fetch-pending-sales-invoice');

        if (res.data && Array.isArray(res.data.data)) {
          const options = res.data.data.map((inv) => ({
            id: inv.id,
            invoice_number: inv.invoice_number,
            name: inv.customer?.name || inv.customer_name || "",
            customerId: inv.id || "",
            raw: inv,
          }));
          setCustomerOptions(options);
        } else {
          setCustomerOptions([]);
        }
      } catch (err) {
        console.error("Error fetching pending invoices:", err);
        setCustomerOptions([]);
      } finally {
        setCustomerLoading(false);
      }
    };

    fetchPendingInvoices();
  }, [receiptType]);

  useEffect(() => {
    const fetchReceiptTypes = async () => {
      try {
        const res = await api.get('/v1/get_type_of_receipts');

        if (res.data && Array.isArray(res.data.data)) {
          setReceiptTypes(res.data.data);
        } else {
          setReceiptTypes([]);
        }
      } catch (err) {
        console.error("Error fetching receipt types:", err);
        setReceiptTypes([]);
      }
    };

    fetchReceiptTypes();
  }, []);

  useEffect(() => {
    const fetchExpenseAccounts = async () => {
      setAccountsLoading(true);
      try {
        // Determine which endpoint to use based on receipt type
        const isGeneralIncome = receiptType?.toLowerCase()?.includes("general income");
        const endpoint = isGeneralIncome 
          ? '/v1/get-income-accounts' 
          : '/v1/get-expense-accounts';
        
        const res = await api.get(endpoint);
        const items = res?.data && Array.isArray(res.data.data) ? res.data.data : [];

        // This endpoint returns the list that should populate the "Account Name"
        // dropdown in the receipt breakdown table.
        // Keep both:
        // - `selectableAccounts`: raw items for the table Account Name dropdown
        // - `expenseOptions`: mapped { value, label } for the top "Debit Account" dropdown
        setSelectableAccounts(items);

        if (items.length > 0) {
          const options = items.map((item) => ({
            value: item.id,
            label: item.gl_name,
            fullObject: item, // Store the full account object
          }));
          setExpenseOptions(options);
        } else {
          setExpenseOptions([]);
        }
      } catch (err) {
        console.error("Failed to fetch accounts", err);
        setSelectableAccounts([]);
        setExpenseOptions([]);
      } finally {
        setAccountsLoading(false);
      }
    };
    fetchExpenseAccounts();
  }, [receiptType]);

  // NOTE:
  // Previously we fetched selectable accounts from `/v1/account` and tried to
  // filter by Income/Receivables. The correct source for the "Account Name"
  // dropdown in this receipt is `/v1/get-expense-account`, which we now use
  // via `fetchExpenseAccounts` above.

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const res = await api.get('/v1/fetch-all-currencies');

        if (res.data && Array.isArray(res.data.data)) {
          setCurrencyOptions(res.data.data);
          const ngnCurrency = res.data.data.find(c => c.code === "NGN");
          if (ngnCurrency) setSelectedCurrency(ngnCurrency.id);
        }
      } catch (err) {
        console.error("Error fetching currencies:", err);
      }
    };
    fetchCurrencies();
  }, []);

  useEffect(() => {
    const fetchPaymentModes = async () => {
      try {
        const res = await api.get('/v1/income/get-payment-method');

        if (res.data && Array.isArray(res.data.data)) {
          setPaymentModes(res.data.data);
        } else {
          setPaymentModes([]);
        }
      } catch (err) {
        console.error("Error fetching payment modes:", err);
        setPaymentModes([]);
      }
    };
    fetchPaymentModes();
  }, []);

  const [debitAccountsLoading, setDebitAccountsLoading] = useState(false);

  useEffect(() => {
      const fetchDebitAccounts = async () => {
      if (!selectedPaymentMode) return;
      const selectedModeName = paymentModes.find(m => m.id == selectedPaymentMode)?.name?.toLowerCase();
      let path = '';
      if (selectedModeName?.includes('bank') || selectedModeName?.includes('transfer') || selectedModeName?.includes('teller')) {
        path = '/v1/bank-accounts';
      } else if (selectedModeName?.includes('cheque') || selectedModeName?.includes('cash')) {
        path = '/v1/cash';
      }
      if (!path) return;

      setDebitAccountsLoading(true);
      try {
        const res = await api.get(path);
        if (res.data && Array.isArray(res.data.data)) {
          const options = res.data.data.map((item) => ({ 
            value: item.id, 
            label: item.gl_name || item.name,
            fullObject: item, // Store the full account object
          }));
          setExpenseOptions(options);
        } else {
          setExpenseOptions([]);
        }
      } catch (err) {
        console.error('Error fetching debit account options:', err);
        setExpenseOptions([]);
      } finally {
        setDebitAccountsLoading(false);
      }
    };

    fetchDebitAccounts();
  }, [selectedPaymentMode, paymentModes]);

  // Accounts are now sourced directly from invoice receivable_gl data,
  // so we don't need to fetch from a separate endpoint.


  // Keep customer search input in sync with selected reference (customer id)
  useEffect(() => {
    const type = receiptType?.toLowerCase();
    if (!type?.includes("credit customer")) return;
    if (!reference) {
      setCustomerSearch("");
      return;
    }
    const match = customerOptions.find((c) => String(c?.customerId) === String(reference));
    if (match) {
      const label = `${match.name || match.customer_name || match.business_name || ""} - ${match.invoice_number || ""}`;
      setCustomerSearch(label);
      setSelectedInvoice(match.invoice_number || "");
    }
  }, [receiptType, reference, customerOptions]);

  // When the selected customer (reference) changes, update the first account row
  // so the account name/code and amount reflect the newly selected customer's invoice.
  useEffect(() => {
    const type = receiptType?.toLowerCase();
    if (!type?.includes("credit customer")) return;
    if (!reference) return;

    const match = customerOptions.find((c) => String(c?.customerId) === String(reference) || String(c?.id) === String(reference));
    if (!match) return;

    const label = `${match.name || match.customer_name || match.business_name || ""} - ${match.invoice_number || ""}`;
    setCustomerSearch(label);
    setSelectedInvoice(match.invoice_number || "");

    const bal = match.raw?.balance ?? match.balance ?? "";
    setTotalAmount(bal);

    const raw = match.raw || {};
    const mapped = createRowsFromInvoice(raw);
    setRows(mapped);
  }, [reference, customerOptions, receiptType]);

  if (!isOpen) return null;

  const addLine = () => {
    setRows((prev) => [
      ...prev,
      { accountId: "", accountDisplay: "", accountCode: "", amount: "", showDropdown: false, accountLocked: false, invoiceItemId: "", itemId: "" },
    ]);
  };

  const duplicateRow = (index) => {
    setRows((prev) => {
      const newRows = [...prev];
      const emptyRow = {
        accountId: "",
        accountDisplay: "",
        accountCode: "",
        amount: "",
        showDropdown: false,
        accountLocked: false,
        invoiceItemId: "",
        itemId: "",
      };
      newRows.splice(index + 1, 0, emptyRow);
      return newRows;
    });
  };

  const removeRow = (index) => {
    setRows((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleChange = (index, field, value) => {
    setRows((prev) => {
      const newRows = [...prev];
      const row = { ...newRows[index] };

      if (field === "showDropdown") {
        row.showDropdown = value;
      } else if (field === "account") {
        row.accountId = value?.id ?? "";
        row.accountDisplay = `${value?.gl_name ?? value?.name ?? ""}`;
        row.accountCode = value?.gl_code ?? value?.code ?? "";
        console.log("Setting account:", row.accountDisplay, "Code:", row.accountCode);
        row.showDropdown = false;
      } else if (field === "accountDisplay") {
        // prevent editing if this row was locked by selecting a customer/invoice
        if (!row.accountLocked) {
          row.accountDisplay = value;
          row.accountId = "";
          row.accountCode = "";
          row.showDropdown = true;
        }
      } else if (field === "amount") {
        row.amount = value;
      }

      newRows[index] = row;
      return newRows;
    });
  };

  const totalDebit = parseFloat(totalAmount || 0);
  const totalCredit = rows.reduce(
    (sum, r) => sum + (parseFloat(r.amount) || 0),
    0
  );
  const isBalanced = totalDebit === totalCredit;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const authToken = localStorage.getItem("authToken");

    if (totalDebit !== totalCredit) {
      Swal.fire({
        icon: "error",
        title: "Unbalanced Entry",
        text: "Total Debit and Total Credit must balance before saving.",
        confirmButtonColor: "#3085d6",
        customClass: { popup: "swal-highest-zindex" },
        didOpen: () => {
          const swalPopup = document.querySelector(".swal2-container");
          if (swalPopup) swalPopup.style.zIndex = "99999";
        },
      });
      return;
    }

    // Validate required fields
    if (!receiptType) {
      Swal.fire({
        icon: "error",
        title: "Missing Receipt Type",
        text: "Please select a receipt type.",
        confirmButtonColor: "#3085d6",
        customClass: { popup: "swal-highest-zindex" },
        didOpen: () => {
          const swalPopup = document.querySelector(".swal2-container");
          if (swalPopup) swalPopup.style.zIndex = "99999";
        },
      });
      return;
    }

    if (!reference || String(reference).trim() === "") {
      Swal.fire({
        icon: "error",
        title: "Missing Received From",
        text: "Please enter who the payment is from.",
        confirmButtonColor: "#3085d6",
        customClass: { popup: "swal-highest-zindex" },
        didOpen: () => {
          const swalPopup = document.querySelector(".swal2-container");
          if (swalPopup) swalPopup.style.zIndex = "99999";
        },
      });
      return;
    }

    if (!transactionDate) {
      Swal.fire({
        icon: "error",
        title: "Missing Date",
        text: "Please select a transaction date.",
        confirmButtonColor: "#3085d6",
        customClass: { popup: "swal-highest-zindex" },
        didOpen: () => {
          const swalPopup = document.querySelector(".swal2-container");
          if (swalPopup) swalPopup.style.zIndex = "99999";
        },
      });
      return;
    }

    if (!selectedPaymentMode) {
      Swal.fire({
        icon: "error",
        title: "Missing Payment Mode",
        text: "Please select a payment mode.",
        confirmButtonColor: "#3085d6",
        customClass: { popup: "swal-highest-zindex" },
        didOpen: () => {
          const swalPopup = document.querySelector(".swal2-container");
          if (swalPopup) swalPopup.style.zIndex = "99999";
        },
      });
      return;
    }

    const debitAccountId =
      typeof debitAccount === "object" && debitAccount !== null
        ? debitAccount.id
        : debitAccount;
    if (
      debitAccountId === undefined ||
      debitAccountId === null ||
      String(debitAccountId).trim() === ""
    ) {
      Swal.fire({
        icon: "error",
        title: "Missing Debit Account",
        text: "Please select a debit account (cash/bank account).",
        confirmButtonColor: "#3085d6",
        customClass: { popup: "swal-highest-zindex" },
        didOpen: () => {
          const swalPopup = document.querySelector(".swal2-container");
          if (swalPopup) swalPopup.style.zIndex = "99999";
        },
      });
      return;
    }

    const typeId =
      receiptType?.id ||
      receiptTypes?.find(
        (t) => t.name === receiptType || t.type_name === receiptType
      )?.id ||
      receiptType;

    // Normalise receipt type for API (avoid sending string "1" when backend expects int)
    const typeIdNum = Number(typeId);
    const typeOfReceipt = Number.isFinite(typeIdNum) ? typeIdNum : typeId;

    // Only set `id` for credit customer receipts (typeId == 2)
    const isCreditCustomerReceipt = typeOfReceipt == 2;

    // `item_id.*` is validated with `exists` on the API ΓÇö `0` fails ("selected ... invalid").
    // For general income etc., mirror `account_id` per row (same ids the API already accepts there).
    // Credit customer: real invoice line ids only.
    if (!isCreditCustomerReceipt) {
      const missingAcct = rows.some(
        (r) => !r.accountId || String(r.accountId).trim() === ""
      );
      if (missingAcct) {
        Swal.fire({
          icon: "error",
          title: "Missing account",
          text: "Select an account (account name) for each line before saving.",
          confirmButtonColor: "#3085d6",
          customClass: { popup: "swal-highest-zindex" },
          didOpen: () => {
            const swalPopup = document.querySelector(".swal2-container");
            if (swalPopup) swalPopup.style.zIndex = "99999";
          },
        });
        return;
      }
    } else {
      const missingLine = rows.some(
        (r) =>
          !(r.invoiceItemId ?? r.itemId) ||
          String(r.invoiceItemId ?? r.itemId ?? "").trim() === ""
      );
      if (missingLine) {
        Swal.fire({
          icon: "error",
          title: "Missing invoice line",
          text: "Each line must be linked to an invoice item for credit customer receipts.",
          confirmButtonColor: "#3085d6",
          customClass: { popup: "swal-highest-zindex" },
          didOpen: () => {
            const swalPopup = document.querySelector(".swal2-container");
            if (swalPopup) swalPopup.style.zIndex = "99999";
          },
        });
        return;
      }
    }

    setIsSaving(true);

    try {
      const itemIds = rows.map((r) => {
        if (isCreditCustomerReceipt) {
          return r.invoiceItemId ?? r.itemId ?? "";
        }
        return r.accountId;
      });

      // `gl_code` here is the selected cash/bank account ΓÇö API validates it like an id (see "selected gl code is invalid").
      const glCodeNumeric = Number(debitAccountId);
      const glCodeForApi = Number.isFinite(glCodeNumeric)
        ? glCodeNumeric
        : String(
            typeof debitAccount === "object" && debitAccount !== null
              ? debitAccount.gl_code ?? debitAccount.code ?? debitAccountId
              : debitAccountId
          );

      const payload = {
        description,
        currency: selectedCurrency?.id || selectedCurrency,
        payment_mode: selectedPaymentMode?.id || selectedPaymentMode,
        transaction_date: transactionDate,
        gl_code: glCodeForApi,
        total_amount: totalCredit,
        account_id: rows.map((row) => row.accountId),
        breakdown_amount: rows.map((row) => parseFloat(row.amount || 0)),
        type_of_receipt: typeOfReceipt,
        ...(typeOfReceipt !== 1 && { item_id: itemIds }), // Exclude item_id for general income (type 1)
        ...(isCreditCustomerReceipt && { id: reference }), // only include id for credit customer
      };

      if (typeOfReceipt == 2) {
        payload.received_from = reference;
      } else if (typeOfReceipt == 3) {
        payload.repayment = reference;
      } else {
        // For general income and other types, use particulars
        payload.particulars = reference;
      }

      // if this is a credit-customer receipt, ensure account_id entries are not empty.
      // fallback: replace empty account ids with the selected customer id so backend receives a value.
      if (typeOfReceipt == 2 && payload.account_id && payload.account_id.length > 0) {
        payload.account_id = payload.account_id.map((a) => {
          if (!a || String(a).trim() === "") return reference || a;
          return a;
        });
      }

      let response;
      // Always use JSON for this endpoint - FormData cannot properly represent empty arrays
      // which causes validation errors when item_id is empty
      
      // Set supporting_document to empty string (backend requirement)
      payload.supporting_document = "";
      
      // Debug: Log the payload being sent
      console.log("Submitting receipt payload:", {
        ...payload,
        item_id: payload.item_id,
        item_id_type: typeof payload.item_id,
        item_id_is_array: Array.isArray(payload.item_id),
      });
      
      response = await api.post('/v1/post-receipt', withCompanyId(payload));

      if (response.status === 200 || response.status === 201) {
        Swal.fire({
          imageUrl: verified,
          title: "Receipt Saved",
          text:
            response.data?.message ||
            "The cash receipt has been successfully saved!",
          showCancelButton: true,
          confirmButtonText: "Print",
          cancelButtonText: "OK",
          confirmButtonColor: "#16A34A",
          cancelButtonColor: "#6b7280",
          customClass: { popup: "swal-highest-zindex" },
          didOpen: () => {
            const swalContainer = document.querySelector(".swal2-container");
            if (swalContainer) swalContainer.style.zIndex = "99999";
          },
        }).then((result) => {
          if (result.isConfirmed) {
            const receiptData = {
              reference,
              transactionDate,
              description,
                  referenceNumber,
              selectedCurrency,
              selectedPaymentMode,
              debitAccount,
              totalAmount: totalCredit,
              rows,
            };

            navigate("/preview", {
              state: { invoice: receiptData },
            });
          } else {
            // Call onSuccess to refresh the table
            if (onSuccess) onSuccess();
            // Close the modal
            onClose();
            // Refresh the page
            window.location.reload();
          }
        });
      }
    } catch (error) {
      console.error("Save error:", error);
      Swal.fire({
        imageUrl: crop,
        title: "Save Failed",
        text:
          typeof error.response?.data?.message === "object"
            ? Object.values(error.response.data.message).flat().join(", ")
            : error.response?.data?.message ||
            "An error occurred while saving the receipt. Please try again.",
        confirmButtonColor: "#000000ff",
        customClass: { popup: "swal-highest-zindex" },
        didOpen: () => {
          const swalContainer = document.querySelector(".swal2-container");
          if (swalContainer) swalContainer.style.zIndex = "99999";
        },
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle dropdown functions - CLEAR SEARCH TERM TO SHOW FULL LIST
  const toggleReceiptTypeDropdown = () => {
    setSearchTerm(""); // Clear search to show full list
    setShowDropdown(!showDropdown);
  };

  const togglePaymentDropdown = () => {
    setPaymentSearch(""); // Clear search to show full list
    setShowPaymentDropdown(!showPaymentDropdown);
  };

  const toggleDebitDropdown = () => {
    setDebitSearch(""); // Clear search to show full list
    setShowDebitDropdown(!showDebitDropdown);
  };

  const toggleAccountDropdown = (index) => {
    console.log("toggleAccountDropdown called for row", index);
    setRows(prev => prev.map((row, i) => {
      if (i === index) {
        const newShowDropdown = !row.showDropdown;
        console.log("Toggling showDropdown for row", i, "from", row.showDropdown, "to", newShowDropdown);
        return { ...row, showDropdown: newShowDropdown };
      }
      return row;
    }));
  };

  // Handle input focus - also clear search to show full list
  const handleReceiptTypeFocus = () => {
    setSearchTerm(""); // Clear search to show full list
    setShowDropdown(true);
  };

  const handlePaymentFocus = () => {
    setPaymentSearch(""); // Clear search to show full list
    setShowPaymentDropdown(true);
  };

  const handleDebitFocus = () => {
    setDebitSearch(""); // Clear search to show full list
    setShowDebitDropdown(true);
  };

  const handleAccountFocus = (index) => {
    setRows(prev => prev.map((row, i) => {
      if (i === index) {
        return { ...row, showDropdown: true };
      }
      return row;
    }));
  };

  return (
    <div className={classes.modalOverlay}>
      <div className={classes.modalContent}>
        <h3 className={classes.title}>Cash Receipt</h3>

        <div className={classes.topRow}>
          <div className={classes.Group3}>
            <label>Type of Receipt</label>
            <div className={classes.searchableDropdown}>
              <div className={classes.inputWithArrow}>
                <input
                  type="text"
                  placeholder="Search or select type..."
                  value={searchTerm}
                  style={{ width: "100%" }}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onFocus={handleReceiptTypeFocus}
                  className={classes.accountInput}
                />
                <span
                  className={classes.dropdownArrow}
                  onClick={toggleReceiptTypeDropdown}
                >
                  Γû╝
                </span>
              </div>

              {showDropdown && (
                <div
                  className={classes.dropdownList}
                  onMouseLeave={() => setShowDropdown(false)}
                >
                  {receiptTypesLoading ? (
                    <div>Loading receipt types...</div>
                  ) : (
                    receiptTypes
                      .filter((type) => {
                        const search = searchTerm.toLowerCase();
                        const name = (type.name || type.type_name || "").toLowerCase();
                        return name.includes(search);
                      })
                      .slice(0, 10)
                      .map((type) => (
                        <div
                          key={type.id}
                          className={classes.dropdownItem}
                          onClick={() => {
                            setReceiptType(type.name || type.type_name);
                            setSearchTerm(type.name || type.type_name);
                            setReference("");
                            setShowDropdown(false);
                          }}
                        >
                          {type.name || type.type_name}
                        </div>
                      ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={classes.Group3}>
            <label>Received From</label>
            {(() => {
              const type = receiptType?.toLowerCase();

              if (
                type === "general income" ||
                type === "other income" ||
                type === "capital contribution"
              ) {
                return (
                  <input
                    type="text"
                    placeholder="Enter payer name"
                    style={{ width: "91%" }}
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                  />
                );
              }

              if (type?.includes("credit customer")) {
                return (
                  <div className={classes.searchableDropdown}>
                    <div className={classes.inputWithArrow}>
                      <input
                        type="text"
                        placeholder={customerLoading ? "Loading customers..." : "Search customer..."}
                        value={customerSearch}
                        disabled={customerLoading}
                        onChange={(e) => {
                          setCustomerSearch(e.target.value);
                          setShowCustomerDropdown(true);
                        }}
                        onFocus={() => setShowCustomerDropdown(true)}
                        className={classes.accountInput}
                      />
                      <span
                        className={classes.dropdownArrow}
                        onClick={() => setShowCustomerDropdown((v) => !v)}
                      >
                        Γû╝
                      </span>
                    </div>

                    {showCustomerDropdown && (
                      <div
                        className={classes.dropdownList}
                        onMouseLeave={() => setShowCustomerDropdown(false)}
                      >
                        {customerLoading ? (
                          <div className={classes.dropdownLoading}>Loading customers...</div>
                        ) : customerOptions.length === 0 ? (
                          <div className={classes.dropdownLoading}>No customers found</div>
                        ) : (
                          customerOptions
                            .filter((cust) => {
                              const q = (customerSearch || "").toLowerCase().trim();
                              if (!q) return true;
                              const name = String(cust?.name || cust?.customer_name || cust?.business_name || "").toLowerCase().trim();
                              const invoice = String(cust?.invoice_number || "").toLowerCase().trim();
                              return name.includes(q) || invoice.includes(q);
                            })
                            .slice(0, 20)
                            .map((cust) => {
                              const label = `${cust?.name || cust?.customer_name || cust?.business_name || ""} - ${cust?.invoice_number || ""}`;
                              return (
                                <div
                                  key={`${cust.customerId}-${cust.invoice_number}-${cust.id}`}
                                  className={classes.dropdownItem}
                                  onClick={() => {
                                    const custId = String(cust.id);
                                    setReference(custId);
                                    setSelectedInvoice(cust.invoice_number || "");
                                    setCustomerSearch(String(label || ""));

                                    // set total amount from invoice balance
                                    const bal = cust.raw?.balance ?? cust.balance ?? "";
                                    setTotalAmount(bal);

                                    // try to create rows for each invoice item
                                    const raw = cust.raw || {};
                                    const mapped = createRowsFromInvoice(raw);
                                    setRows(mapped);

                                    setShowCustomerDropdown(false);
                                  }}
                                >
                                  {label}
                                </div>
                              );
                            })
                        )}
                      </div>
                    )}
                  </div>
                );
              }

              if (type?.includes("loan repayment")) {
                return (
                  <select
                    value={reference}
                    onChange={(e) => setReference(e.target.value)}
                    style={{ width: "91%" }}
                  >
                    <option value="">Select Loan</option>
                    <option value="loan-1">Loan #001 - Adewale</option>
                    <option value="loan-2">Loan #002 - Bright</option>
                    <option value="loan-3">Loan #003 - Stella</option>
                  </select>
                );
              }

              return null;
            })()}
          </div>

          <div className={classes.Group}>
            <label>Date</label>
            <input
              type="date"
              value={transactionDate}
              onChange={(e) => setTransactionDate(e.target.value)}
              required
            />
          </div>

          <div className={classes.Group}>
            <label>Currency</label>
            <select
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
            >
              <option value="">Select Currency</option>
              {currencyOptions.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code || c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className={classes.topRows}>
          <div className={classes.Group1}>
            <label>Description</label>
            <input
              type="text"
              placeholder="Enter description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className={classes.Group2}>
            <label>Mode of Payment</label>
            <div className={classes.searchableDropdown}>
              <div className={classes.inputWithArrow}>
                <input
                  type="text"
                  placeholder="Search or select payment mode..."
                  value={paymentSearch}
                  onChange={(e) => setPaymentSearch(e.target.value)}
                  onFocus={handlePaymentFocus}
                  className={classes.accountInput}
                />
                <span
                  className={classes.dropdownArrow}
                  onClick={togglePaymentDropdown}
                >
                  Γû╝
                </span>
              </div>

              {showPaymentDropdown && (
                <div
                  className={classes.dropdownList}
                  onMouseLeave={() => setShowPaymentDropdown(false)}
                >
                  {paymentModes.length === 0 ? (
                    <div>No payment modes found</div>
                  ) : (
                    paymentModes
                      .filter((m) =>
                        m.name.toLowerCase().includes(paymentSearch.toLowerCase())
                      )
                      .slice(0, 10)
                      .map((m) => (
                        <div
                          key={m.id}
                          className={classes.dropdownItem}
                          onClick={() => {
                            setSelectedPaymentMode(m.id);
                            setPaymentSearch(m.name);
                            setShowPaymentDropdown(false);
                          }}
                        >
                          {m.name}
                        </div>
                      ))
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={classes.Group2}>
            <label>Debit Account</label>
            <div className={classes.searchableDropdown}>
              <div className={classes.inputWithArrow}>
                <input
                  type="text"
                  placeholder="Search or select debit account..."
                  value={debitSearch}
                  onChange={(e) => setDebitSearch(e.target.value)}
                  onFocus={handleDebitFocus}
                  className={classes.accountInput}
                  disabled={debitAccountsLoading}
                />
                <span
                  className={classes.dropdownArrow}
                  onClick={toggleDebitDropdown}
                >
                  Γû╝
                </span>
              </div>

              {showDebitDropdown && (
                <div
                  className={classes.dropdownList}
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    maxHeight: "400px",
                    overflowY: "auto",
                    background: "#fff",
                    zIndex: 9999,
                  }}
                  onMouseLeave={() => setShowDebitDropdown(false)}
                >
                  {debitAccountsLoading ? (
                    <div>Loading accounts...</div>
                  ) : (
                    expenseOptions
                      .filter((acc) =>
                        acc.label.toLowerCase().includes(debitSearch.toLowerCase())
                      )
                      .map((acc) => (
                        <div
                          key={acc.value}
                          className={classes.dropdownItem}
                          onClick={() => {
                            setDebitAccount(acc.fullObject || { id: acc.value }); // Store full object
                            setDebitSearch(acc.label);
                            setShowDebitDropdown(false);
                          }}
                        >
                          {acc.label}
                        </div>
                      ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className={classes.top}>
          <div className={classes.Group1}>
            <label>Total Amount</label>
            <input
              type="number"
              placeholder="Enter Amount"
              style={{ textAlign: "right" }}
              value={totalAmount}
              onChange={(e) => setTotalAmount(e.target.value)}
            />
          </div>

          <div className={classes.Group2}>
            <label>Loan Account</label>
            <select
              value={selectedLoanAccount}
              onChange={(e) => setSelectedLoanAccount(e.target.value)}
              disabled
            >
              <option value="">Select Loan Account (Coming Soon)</option>
              <option value="loan-1">Loan #001 - Placeholder</option>
              <option value="loan-2">Loan #002 - Placeholder</option>
            </select>
          </div>

          <div className={classes.Group2}>
            <label>Reference/Income/Loan No.</label>
            <input
              type="text"
              placeholder="Enter reference number"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
            />
          </div>

          {(() => {
            const selectedModeName = paymentModes.find(
              (m) => m.id == selectedPaymentMode
            )?.name?.toLowerCase();

            if (selectedModeName?.includes("loan")) {
              return (
                <div className={classes.Group2}>
                  <label>Outstanding</label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={outstandingAmount || ""}
                    disabled
                  />
                </div>
              );
            }

            return null;
          })()}
        </div>

        <div className={classes.Group2}>
          <label>Supporting Document</label>
          <input
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            className={classes.fileInput}
          />
          {supportingError && (
            <div className={classes.errorText} style={{ color: 'red', marginTop: 6 }}>{supportingError}</div>
          )}
          {supportingDocument && (
            <div className={classes.fileInfo} style={{ marginTop: 6 }}>
              {supportingDocument.name} <button type="button" onClick={() => { setSupportingDocument(null); setSupportingError(""); }}>Remove</button>
            </div>
          )}
        </div>

        <table className={classes.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Account Name</th>
              <th>Account Code</th>
              <th>Amount</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td className={classes.accountCell}>
                  <div className={classes.searchableDropdown}>
                    <div className={classes.inputWithArrow}>
                      <input
                        type="text"
                        value={row.accountDisplay}
                        onChange={(e) => {
                          console.log("Input change for row", index, "value:", e.target.value);
                          handleChange(index, "accountDisplay", e.target.value);
                        }}
                        onFocus={() => { 
                          console.log("Focus on row", index, "accountLocked:", row.accountLocked);
                          if (!row.accountLocked) handleAccountFocus(index); 
                        }}
                        className={classes.accountInput}
                        placeholder="Search or type account..."
                        disabled={!!row.accountLocked}
                      />
                      <span
                        className={classes.dropdownArrow}
                        onClick={() => { 
                          console.log("Arrow clicked for row", index);
                          if (!row.accountLocked) toggleAccountDropdown(index); 
                        }}
                      >
                        Γû╝
                      </span>
                    </div>

                    {row.showDropdown && (
                      <div
                        className={classes.dropdownList}
                        style={{
                          position: "absolute",
                          top: "100%",
                          left: 0,
                          right: 0,
                          maxHeight: "400px",
                          overflowY: "auto",
                          background: "#fff",
                          zIndex: 9999,
                        }}
                        onMouseLeave={() => handleChange(index, "showDropdown", false)}
                      >
                        {row.accountLocked ? (
                          <div className={classes.dropdownLoading}>
                            No accounts available. Accounts are populated from invoice data.
                          </div>
                        ) : accountsLoading ? (
                          <div className={classes.dropdownLoading}>
                            <Spinner animation="border" size="sm" />
                          </div>
                        ) : selectableAccounts.length === 0 ? (
                          <div className={classes.dropdownLoading}>No accounts available</div>
                        ) : (
                          selectableAccounts
                            .filter((acc) => {
                              const search = (row.accountDisplay || "").toLowerCase().trim();
                              if (!search) return true;
                              const name = (acc.gl_name || acc.name || "").toLowerCase().trim();
                              const code = (acc.gl_code || acc.code || "").toLowerCase().trim();
                              return name.includes(search) || code.includes(search);
                            })
                            .slice(0, 20)
                            .map((acc) => {
                              console.log("Rendering account option:", acc);
                              return (
                                <div
                                  key={acc.id}
                                  className={classes.dropdownItem}
                                  onClick={() => {
                                    console.log("Selected account:", acc);
                                    handleChange(index, "account", acc);
                                    handleChange(index, "showDropdown", false);
                                  }}
                                >
                                  {acc.gl_name || acc.name} - {acc.gl_code || acc.code}
                                </div>
                              );
                            })
                        )}
                      </div>
                    )}
                  </div>
                </td>

                <td>
                  <input
                    type="text"
                    value={row.accountCode}
                    readOnly
                    className={classes.readOnlyInput}
                  />
                </td>

                <td>
                  <input
                    type="number"
                    className={classes.creditInput}
                    value={row.amount}
                    onChange={(e) =>
                      handleChange(index, "amount", e.target.value)
                    }
                  />
                </td>

                <td>
                  <button
                    onClick={() => duplicateRow(index)}
                    className={classes.linkBtn}
                    disabled={
                      receiptType?.toLowerCase()?.includes("credit customer") ||
                      receiptType?.toLowerCase()?.includes("loan repayment")
                    }
                    style={{
                      opacity:
                        receiptType?.toLowerCase()?.includes("credit customer") ||
                          receiptType?.toLowerCase()?.includes("loan repayment")
                          ? 0.6
                          : 1,
                      cursor:
                        receiptType?.toLowerCase()?.includes("credit customer") ||
                          receiptType?.toLowerCase()?.includes("loan repayment")
                          ? "not-allowed"
                          : "pointer",
                    }}
                  >
                    Duplicate
                  </button>

                  <button
                    onClick={() => removeRow(index)}
                    className={classes.removeBtn}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={classes.actionRow}>
          <button
            className={classes.addLineBtn}
            onClick={addLine}
          >
            + Add Line
          </button>
        </div>

        <div className={classes.totalRow}>
          <div className={classes.totalBox}>
            <span>Total Debit</span>
            <strong>{totalDebit.toFixed(2)}</strong>
          </div>
          <div className={classes.totalBox}>
            <span>Total Credit</span>
            <strong>{totalCredit.toFixed(2)}</strong>
          </div>
        </div>

        <div className={classes.footerButtons}>
          <button
            className={classes.resetBtn}
            onClick={() =>
              setRows([
                {
                  accountId: "",
                  accountDisplay: "",
                  accountCode: "",
                  amount: "",
                  showDropdown: false,
                  accountLocked: false,
                  invoiceItemId: "",
                  itemId: "",
                },
              ])
            }
          >
            Reset
          </button>
          <button className={classes.printBtn} onClick={handlePrinter}>
            Print
          </button>
          <button
            className={classes.submitBtn}
            onClick={handleSubmit}
            disabled={isSaving || !isBalanced}
            style={{
              backgroundColor: isSaving || !isBalanced ? "#ccc" : "#16A34A",
              cursor: isSaving || !isBalanced ? "not-allowed" : "pointer",
            }}
          >
            {isSaving ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <Spinner animation="border" size="sm" /> Saving...
              </span>
            ) : (
              "Save Receipt"
            )}
          </button>
        </div>

        <button className={classes.closeBtn} onClick={onClose}>
          ├ù
        </button>
      </div>
    </div>
  );
}

export default CashReciept;
